import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { AuthToken } from '@shared/signup/models';
import { LocalStorageService } from '@tablesready/frontend-storage';

@Injectable({providedIn: 'root'})
export class TokenService {
  tokens$: BehaviorSubject<AuthToken[] | null>;
  private readonly tokensKey = 'tokens';
  private readonly activeTokenKey = 'tr-auth-token';
  private readonly anonTokenKey = 'tr-anon-token';

  constructor (private readonly localStorageService: LocalStorageService) {
    this.tokens$ = new BehaviorSubject(this.getAll());
  }

  setTokens(tokens: (AuthToken & {lastUpdatedAt?: number})[]): void {
    this.localStorageService.set(this.tokensKey, tokens.map<TokenModel>(token => ({...token, lastUpdatedAt: token.lastUpdatedAt || Date.now()})));
    this.tokens$.next(tokens);
  }

  getAll(): TokenModel[] {
    return this.localStorageService.get(this.tokensKey);
  }

  setActiveByLocationId(locationId: string): void {
    const token = this.getAll().find(tokenData => tokenData.locationId === locationId);
    if (token) {
      this.setActive(token);
    }
  }

  setActive(token: AuthToken): void {
    this.localStorageService.set(this.activeTokenKey, {
      ...token,
      expiresIn: token.expiresIn * 1000 + Date.now(),
    });
  }

  getActive(): TokenModel | null {
    try {
      const token = this.localStorageService.get<TokenModel>(this.activeTokenKey);
      if (!Number.isNaN(token.expiresIn) && token.expiresIn <= Date.now()) {
        // Token is stale
        this.clear();

        return null;
      }

      return token;
    } catch (err) {
      return null;
    }
  }

  add(tokens: AuthToken[]): void {
    this.setTokens([...this.getAll(), ...tokens.map<TokenModel>(token => ({...token, lastUpdatedAt: Date.now()}))]);
    this.tokens$.next(this.getAll());
  }

  removeByLocationIds(ids: string[]): void {
    let tokenToRemove: AuthToken = null;
    let isActiveTokenRemoving = false;
    const tokens: AuthToken[] = [];
    for (const token of this.getAll()) {
      if (ids.indexOf(token.locationId) > -1) {
        tokenToRemove = token;
        isActiveTokenRemoving = token.locationId === this.getActive().locationId || isActiveTokenRemoving;
        continue;
      }
      tokens.push(token);
    }

    if (!tokenToRemove) {
      return;
    }

    if (isActiveTokenRemoving && tokens.length) {
      const tokenWithLocation = tokens.find(token => token.locationId);
      if (tokenWithLocation) {
        this.setActiveByLocationId(tokenWithLocation.locationId);
      } else {
        this.setActive(tokens[0]);
      }
    }
    this.setTokens(tokens);
  }

  update(token: AuthToken): void {
    const tokens = this.getAll().filter(_token => _token.locationId !== token.locationId);
    this.setTokens([...tokens, token]);
  }

  clear(): void {
    this.localStorageService.remove(this.activeTokenKey, this.tokensKey);
    this.tokens$.next(null);
  }

  getAnonToken(): string {
    try {
      const token = this.localStorageService.get<TokenModel>(this.anonTokenKey);
      if (!Number.isNaN(token.expiresIn) && token.expiresIn <= Date.now()) {
        // Token is stale
        this.localStorageService.remove(this.anonTokenKey);

        return null;
      }

      return token.token;
    } catch (err) {
      return null;
    }
  }

  setAnonToken(token: string, expiresIn: number): void {
    const currentDate = Date.now();
    const expiresAt = expiresIn + currentDate;
    this.localStorageService.set(this.anonTokenKey, {token, expiresAt, lastUpdatedAt: currentDate});
  }
}

export interface TokenModel extends AuthToken {
  lastUpdatedAt: number;
}
